import { capitalize } from '@buyerflo/core/helperFunctions/string'
import { formatAddress } from '@buyerflo/core/schemas/generic'
import { VendorSchema, VendorType } from '@buyerflo/core/schemas/vendor'
import {
    AccountCircleIcon,
    BusinessCenterIcon,
    DeleteRounded,
    EmailIcon,
    MapPinIcon,
    PhoneIcon,
} from 'components/icons'
import { SlotInput } from 'components/input'
import { Label } from 'components/label'
import { MobileFriendlyStack } from 'components/stack'
import { Controller, useFormContext } from 'react-hook-form'
import Button from 'components/button'
import { RadioChips } from '@buyerflo/ui/components/RadioChips'
import { SizableText, View, XStack, YStack } from 'tamagui'
import { useEditMode } from './editMode'

interface BuyerListProps {
    name: string
    buyers: Array<VendorSchema & { id: string }>
    removeBuyer: (index: number) => void
    buyerType: VendorType
    setBuyerType: (type: VendorType) => void
    extraFields?: (buyer: VendorSchema, index: number) => React.ReactNode
    disabled?: boolean
    showAlert?: boolean
    hideTitle?: boolean
}

const ORDINALS = ['Primary', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth']

/** A single read-only contact line: faint leading icon + value (mono for codes/contacts). */
function ContactLine({
    icon: Icon,
    mono,
    children,
}: {
    icon: typeof EmailIcon
    mono?: boolean
    children: React.ReactNode
}) {
    return (
        <XStack
            alignItems="center"
            gap="$2.5"
        >
            <Icon
                size={15}
                color="$colorFaint"
            />
            <SizableText
                color="$color"
                {...(mono
                    ? { fontFamily: '$mono', style: { fontSize: 13 } }
                    : { fontSize: '$4' })}
            >
                {children}
            </SizableText>
        </XStack>
    )
}

export default ({
    name,
    buyers,
    removeBuyer,
    buyerType,
    setBuyerType,
    extraFields,
    disabled,
    hideTitle,
}: BuyerListProps) => {
    const { control, setValue, watch } = useFormContext()
    const { editMode } = useEditMode()

    const fieldName = name.toLocaleLowerCase()
    const isCompany = buyerType == 'company'

    // Live values drive the group header title and the read-only contact lines.
    const liveBuyers = watch(`${fieldName}s`) as Array<VendorSchema> | undefined

    return (
        <YStack>
            {!hideTitle && (
                <SizableText
                    fontSize="$7"
                    fontWeight="700"
                    letterSpacing={-0.3}
                    marginBottom="$3"
                >
                    {name}s
                </SizableText>
            )}

            {/* Buyer Type — shown in both modes */}
            <YStack>
                <Label>{name} Type</Label>
                {editMode ? (
                    <RadioChips
                        value={buyerType}
                        onChange={value => {
                            setBuyerType(value as any)
                            buyers.forEach((_, i) => {
                                setValue(`${fieldName}s.${i}.vendorType`, value as any)
                            })
                        }}
                        disabled={disabled}
                        options={[
                            { value: 'individual', label: 'Individual' },
                            { value: 'company', label: 'Company' },
                        ]}
                    />
                ) : (
                    <SizableText
                        fontSize="$4"
                        color="$color"
                    >
                        {capitalize(buyerType)}
                    </SizableText>
                )}
            </YStack>

            <View
                height={1}
                backgroundColor="$borderColor"
                marginTop="$3.5"
                marginBottom="$2"
            />

            {buyers.map((buyer, i) => {
                const v = (liveBuyers?.[i] ?? buyer) as VendorSchema
                const company = v?.vendorType === 'company' ? v : undefined
                const personName = `${v?.firstName ?? ''} ${v?.lastName ?? ''}`.trim()
                const fullName = (company?.companyName || personName) || 'New buyer'
                const ordinalLabel = `${ORDINALS[i] ?? `Buyer ${i + 1}`} ${fieldName}`
                const address =
                    v?.address &&
                    (typeof v.address === 'string' ? v.address : formatAddress(v.address))

                return (
                    <YStack
                        key={buyer.id}
                        marginTop={i === 0 ? '$2' : 20}
                        paddingTop={i === 0 ? 0 : 20}
                        borderTopWidth={i === 0 ? 0 : 1}
                        borderColor="$borderColor"
                    >
                        {/*
                          Header. Read-only: full name as title, ordinal sub when
                          multiple. Edit: no name (it's echoed in the fields below) —
                          only an ordinal label when there are multiple buyers.
                        */}
                        {(editMode ? buyers.length > 1 : true) && (
                            <XStack
                                alignItems="center"
                                gap="$2.5"
                                marginBottom="$3.5"
                            >
                                <YStack
                                    flex={1}
                                    minWidth={0}
                                >
                                    {editMode ? (
                                        <SizableText
                                            fontSize="$4"
                                            fontWeight="600"
                                            letterSpacing={-0.1}
                                            numberOfLines={1}
                                        >
                                            {ordinalLabel}
                                        </SizableText>
                                    ) : (
                                        <>
                                            <SizableText
                                                fontSize="$4"
                                                fontWeight="600"
                                                letterSpacing={-0.1}
                                                numberOfLines={1}
                                            >
                                                {fullName}
                                            </SizableText>
                                            {buyers.length > 1 && (
                                                <SizableText
                                                    fontSize="$2"
                                                    color="$colorMuted"
                                                >
                                                    {ordinalLabel}
                                                </SizableText>
                                            )}
                                        </>
                                    )}
                                </YStack>
                                {editMode && !disabled && buyers.length > 1 && (
                                    <Button
                                        size="$2"
                                        chromeless
                                        circular
                                        icon={<DeleteRounded size={15} color="$colorMuted" />}
                                        onPress={() => removeBuyer(i)}
                                    />
                                )}
                            </XStack>
                        )}

                        {editMode ? (
                            <YStack gap="$3.5">
                                {isCompany && (
                                    <>
                                        <Controller
                                            name={`${fieldName}s.${i}.companyName`}
                                            disabled={disabled}
                                            control={control}
                                            render={field => (
                                                <SlotInput
                                                    label="Company Name"
                                                    {...field}
                                                />
                                            )}
                                        />
                                        <MobileFriendlyStack width="100%">
                                            <View $sm={{ flex: 1 }}>
                                                <Controller
                                                    name={`${fieldName}s.${i}.abn`}
                                                    disabled={disabled}
                                                    control={control}
                                                    render={field => (
                                                        <SlotInput
                                                            label="ABN"
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            </View>
                                            <View $sm={{ flex: 1 }}>
                                                <Controller
                                                    name={`${fieldName}s.${i}.acn`}
                                                    disabled={disabled}
                                                    control={control}
                                                    render={field => (
                                                        <SlotInput
                                                            label="ACN"
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            </View>
                                        </MobileFriendlyStack>
                                    </>
                                )}
                                <MobileFriendlyStack width="100%">
                                    <View $sm={{ flex: 1 }}>
                                        <Controller
                                            name={`${fieldName}s.${i}.firstName`}
                                            disabled={disabled}
                                            control={control}
                                            render={field => (
                                                <SlotInput
                                                    label="First Name"
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </View>
                                    <View $sm={{ flex: 1 }}>
                                        <Controller
                                            name={`${fieldName}s.${i}.lastName`}
                                            disabled={disabled}
                                            control={control}
                                            render={field => (
                                                <SlotInput
                                                    label="Last Name"
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </View>
                                </MobileFriendlyStack>
                                <MobileFriendlyStack width="100%">
                                    <View $sm={{ flex: 1 }}>
                                        <Controller
                                            name={`${fieldName}s.${i}.email`}
                                            disabled={disabled}
                                            control={control}
                                            render={field => (
                                                <SlotInput
                                                    label="Email"
                                                    startDecorator={<EmailIcon size={14} />}
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </View>
                                    <View $sm={{ flex: 1 }}>
                                        <Controller
                                            name={`${fieldName}s.${i}.mobileNumber`}
                                            disabled={disabled}
                                            control={control}
                                            render={field => (
                                                <SlotInput
                                                    label="Phone Number"
                                                    startDecorator={<PhoneIcon size={14} />}
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </View>
                                </MobileFriendlyStack>
                                {extraFields && extraFields(buyer, i)}
                            </YStack>
                        ) : (
                            <YStack gap="$2.5">
                                {company?.abn && (
                                    <ContactLine
                                        icon={BusinessCenterIcon}
                                        mono
                                    >
                                        ABN {company.abn}
                                    </ContactLine>
                                )}
                                {company?.acn && (
                                    <ContactLine
                                        icon={BusinessCenterIcon}
                                        mono
                                    >
                                        ACN {company.acn}
                                    </ContactLine>
                                )}
                                {company && personName && (
                                    <ContactLine icon={AccountCircleIcon}>{personName}</ContactLine>
                                )}
                                {v?.email && (
                                    <ContactLine icon={EmailIcon}>{v.email}</ContactLine>
                                )}
                                {v?.mobileNumber && (
                                    <ContactLine icon={PhoneIcon}>{v.mobileNumber}</ContactLine>
                                )}
                                {address && <ContactLine icon={MapPinIcon}>{address}</ContactLine>}
                            </YStack>
                        )}
                    </YStack>
                )
            })}
        </YStack>
    )
}
